const weekdayMessage = 'Please choose a Tuesday–Saturday date.';

export function flowerToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const part = name => parts.find(item => item.type === name).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function flowerDateError(value, today = flowerToday()) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return weekdayMessage;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return weekdayMessage;
  if (value < today) return 'Please choose today or a future Tuesday–Saturday date.';
  if (date.getUTCDay() < 2) return weekdayMessage;
  return '';
}

export function validateFlowerDate(input, showMessage = true) {
  const calendar = input.closest('[data-fm-calendar]');
  const message = flowerDateError(input.value);
  const translated = message ? (input.value && input.value < flowerToday() ? calendar?.dataset.pastError : calendar?.dataset.weekdayError) || message : '';
  const trigger = calendar?.querySelector('[data-fm-date-trigger]');
  trigger?.setAttribute('aria-invalid', message && showMessage ? 'true' : 'false');
  const error = calendar?.querySelector('[data-fm-date-error]');
  if (error) {
    error.textContent = showMessage ? translated : '';
    error.hidden = !showMessage || !message;
  }
  return !message;
}

export function validateFlowerOrderForm(form) {
  const input = form.querySelector('[data-fm-requested-date]');
  if (!input || validateFlowerDate(input)) return true;
  input.closest('[data-fm-calendar]')?.querySelector('[data-fm-date-trigger]')?.focus();
  return false;
}

function initializeCalendar(calendar) {
  if (calendar.dataset.initialized) return;
  calendar.dataset.initialized = 'true';
  const input = calendar.querySelector('[data-fm-requested-date]');
  const details = calendar.querySelector('details');
  const days = calendar.querySelector('[data-fm-calendar-days]');
  const trigger = calendar.querySelector('[data-fm-date-trigger]');
  const selected = calendar.querySelector('[data-fm-date-selected]');
  let month = new Date(`${flowerToday().slice(0, 7)}-01T00:00:00Z`);
  const format = options => new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', { ...options, timeZone: 'UTC' });
  const render = () => {
    const today = flowerToday();
    calendar.querySelector('[data-fm-month-label]').textContent = format({ month: 'long', year: 'numeric' }).format(month);
    calendar.querySelector('[data-fm-month="-1"]').disabled = month.toISOString().slice(0,7) <= today.slice(0,7);
    days.replaceChildren();
    for (let blank = 0; blank < month.getUTCDay(); blank++) days.append(document.createElement('span'));
    const count = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth()+1,0)).getUTCDate();
    for (let day = 1; day <= count; day++) {
      const date = new Date(Date.UTC(month.getUTCFullYear(),month.getUTCMonth(),day));
      const value = date.toISOString().slice(0,10);
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = String(day);
      button.dataset.fmDate = value;
      button.disabled = !!flowerDateError(value, today);
      button.setAttribute('aria-label', format({weekday:'long',year:'numeric',month:'long',day:'numeric'}).format(date) + (button.disabled ? ` — ${calendar.dataset.unavailable}` : ''));
      button.setAttribute('aria-pressed', String(input.value === value));
      if (value === today) button.setAttribute('aria-current','date');
      days.append(button);
    }
  };
  details.addEventListener('toggle', () => { if (details.open) render(); });
  calendar.addEventListener('click', event => {
    const button = event.target.closest('button');
    if (!button || button.disabled) return;
    if (button.dataset.fmMonth) {
      month.setUTCMonth(month.getUTCMonth() + Number(button.dataset.fmMonth));
      render();
      return;
    }
    if (!button.dataset.fmDate || flowerDateError(button.dataset.fmDate)) return;
    input.value = button.dataset.fmDate;
    selected.textContent = format({month:'short',day:'numeric',year:'numeric'}).format(new Date(`${input.value}T00:00:00Z`));
    input.dispatchEvent(new Event('change',{bubbles:true}));
    validateFlowerDate(input);
    render();
    details.open = false;
    trigger.focus();
  });
  calendar.addEventListener('keydown', event => {
    if (event.key === 'Escape') { details.open = false; trigger.focus(); }
    if (!event.target.matches('[data-fm-date]')) return;
    const offset = {ArrowLeft:-1,ArrowRight:1,ArrowUp:-7,ArrowDown:7}[event.key];
    if (!offset) return;
    event.preventDefault();
    const buttons = [...days.querySelectorAll('button')];
    let index = buttons.indexOf(event.target) + offset;
    while (buttons[index]?.disabled) index += Math.sign(offset);
    buttons[index]?.focus();
  });
  render();
  validateFlowerDate(input, false);
}

if (typeof document !== 'undefined') {
  const initialize = () => document.querySelectorAll('[data-fm-calendar]').forEach(initializeCalendar);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once:true});
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
  new MutationObserver(initialize).observe(document.documentElement, {childList:true,subtree:true});
  document.addEventListener('submit', event => {
    if (event.target.matches?.('form') && !validateFlowerOrderForm(event.target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
}
